import React, { useState, useRef, useEffect, SetStateAction } from 'react';
import { css } from '@emotion/core';
import AutosizeInput from 'react-input-autosize';

import ClickableContainer from 'components/clickable-container';
import { isRtl } from 'styles/global_defaults/media-queries';
import { gray5, primary } from 'styles/global_defaults/colors';
import {
  doubleSpacing,
  quarterSpacing,
  threeQuartersSpacing,
  tripleSpacing,
} from 'styles/global_defaults/scaffolding';
import { ScaleLimits } from './types';

type LimitSettingInputProps = {
  isMinLimitInput?: boolean;
  setLimits: (limits: SetStateAction<ScaleLimits>) => void;
  limits: ScaleLimits;
  isFocused: boolean;
  addDataQa: string;
  editDataQa: string;
  onBlur: (e, setInputMode, setIsFocused, isMinLimitInput) => void;
};

const LimitSettingInput = (props: LimitSettingInputProps) => {
  const {
    setLimits,
    isMinLimitInput,
    limits,
    isFocused,
    addDataQa,
    editDataQa,
    onBlur,
  } = props;

  const [inputMode, setInputMode] = useState(true);
  const [inputContent, setInputContent] = useState(
    isMinLimitInput ? limits.min.value : limits.max.value,
  );

  const setIsFocused = (focused: boolean) => {
    if (isMinLimitInput) {
      setLimits((currentLimits) => ({
        max: currentLimits.max,
        min: {
          ...currentLimits.min,
          isInputFocused: focused,
        },
      }));
    } else {
      setLimits((currentLimits) => ({
        min: currentLimits.min,
        max: {
          ...currentLimits.max,
          isInputFocused: focused,
        },
      }));
    }
  };

  useEffect(() => {
    if (isMinLimitInput) {
      if (limits.min.value) {
        setInputMode(false);
        return;
      }
    }
    if (limits.max.value) {
      setInputMode(false);
    }
  }, [isMinLimitInput, limits.max.value, limits.min.value]);

  const inputRef = useRef(null);

  const switchToInputMode = () => {
    setInputMode(true);
    setIsFocused(true);
    setInputContent(isMinLimitInput ? limits.min.value : limits.max.value);
    setTimeout(() => {
      inputRef?.current?.focus();
    });
  };

  const getBorderColor = () => {
    if (isFocused) {
      return primary;
    }
    return gray5;
  };

  const styles = css`
    .input-mode {
      border: 1px solid ${getBorderColor()};
      border-radius: ${quarterSpacing}px;
      position: relative;
      box-shadow: 0px 4px 10px rgba(0, 0, 0, 0.1);
      input {
        height: ${tripleSpacing}px !important;
        border-radius: ${quarterSpacing}px;
        background-color: white;
        border: 1px solid ${gray5};
        padding: 0px 2px 0px 2px !important;
        text-align: center;
        &:focus-visible {
          outline: none !important;
        }
      }
    }
    .view-mode {
      border: 1px solid ${gray5};
      border-radius: ${quarterSpacing}px;
      height: ${tripleSpacing}px !important;
      min-width: ${doubleSpacing}px;
      position: relative;
      box-shadow: 0px 4px 10px rgba(0, 0, 0, 0.1);
    }
    .arrow {
      position: absolute;
      width: ${threeQuartersSpacing}px;
      height: ${threeQuartersSpacing}px;
      // Adjusting border-width for RTL: swaps left ↔ right to maintain correct border placement.
      border-width: ${isRtl() ? '0px 1px 1px 0px' : '0px 0px 1px 1px'};
      border-style: solid;
      border-color: ${getBorderColor()};
      background-color: white;
      transform: translate(-50%, -50%) rotate(-45deg);
      left: 50%;
    }
  `;

  return (
    <div css={styles}>
      {inputMode ? (
        <div className='input-mode' css={styles}>
          <AutosizeInput
            ref={inputRef}
            tabIndex={0}
            onBlur={e => onBlur(e, setInputMode, setIsFocused, isMinLimitInput)}
            type='number'
            className='p-3'
            placeholder='0'
            value={inputContent}
            onFocus={() => setIsFocused(true)}
            onChange={(e) => setInputContent(e.target.value)}
            data-qa={addDataQa}
          />
          <div className='arrow' />
        </div>
      ) : (
        <div>
          <ClickableContainer
            onClick={switchToInputMode}
            className='bold text-large-regular view-mode align-items-center justify-content-center'
            data-qa={editDataQa}
          >
            {isMinLimitInput ? limits.min.value : limits.max.value}
          </ClickableContainer>
          <div className='arrow' />
        </div>
      )}
    </div>
  );
};

export default LimitSettingInput;
